import React from 'react';
import { Button } from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { TFunction } from 'i18next';

export type ApprovalNoticeType =
  | 'choXacNhan'
  | 'phuTrach'
  | 'boPhan'
  | 'hoTro'
  | 'rejected'
  | 'reconfirm';

const VARIANT: Record<
  ApprovalNoticeType,
  { border: string; bg: string; iconBg: string; iconColor: string; titleColor: string }
> = {
  choXacNhan: {
    border: 'border-amber-200',
    bg: 'bg-amber-50/90',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-900',
  },
  phuTrach: {
    border: 'border-blue-200',
    bg: 'bg-blue-50/90',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-900',
  },
  boPhan: {
    border: 'border-blue-200',
    bg: 'bg-blue-50/90',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-900',
  },
  hoTro: {
    border: 'border-sky-200',
    bg: 'bg-sky-50/90',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    titleColor: 'text-sky-900',
  },
  rejected: {
    border: 'border-red-200',
    bg: 'bg-red-50/90',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    titleColor: 'text-red-900',
  },
  reconfirm: {
    border: 'border-blue-200',
    bg: 'bg-blue-50/90',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-900',
  },
};

const ICON: Record<ApprovalNoticeType, React.ReactNode> = {
  choXacNhan: <ClockCircleOutlined style={{ fontSize: 18 }} />,
  phuTrach: <UserOutlined style={{ fontSize: 18 }} />,
  boPhan: <TeamOutlined style={{ fontSize: 18 }} />,
  hoTro: <TeamOutlined style={{ fontSize: 18 }} />,
  rejected: <CloseCircleOutlined style={{ fontSize: 18 }} />,
  reconfirm: <CheckCircleOutlined style={{ fontSize: 18 }} />,
};

export interface ApprovalNoticeBannerProps {
  type: ApprovalNoticeType;
  message: string;
  /** Lý do từ chối — hiển thị dưới message khi type = rejected. */
  reason?: string | null;
  updating?: boolean;
  t: TFunction;
  onApprove?: (isHoTro: boolean) => void;
  onReject?: () => void;
}

/** Banner xác nhận công việc (chờ / phụ trách / hỗ trợ / từ chối / nhận lại phụ trách) */
export const ApprovalNoticeBanner: React.FC<ApprovalNoticeBannerProps> = ({
  type,
  message,
  reason,
  updating = false,
  t,
  onApprove,
  onReject,
}) => {
  const style = VARIANT[type];
  const canApproveReject = type === 'phuTrach' || type === 'boPhan' || type === 'hoTro';
  const canApproveOnly = type === 'reconfirm';
  const isHoTro = type === 'hoTro';
  const reasonText = String(reason ?? '').trim();

  return (
    <div
      className={[
        'mb-4 rounded-xl border px-4 py-3.5 shadow-sm',
        style.border,
        style.bg,
      ].join(' ')}
    >
      {canApproveOnly ? (
        <div className="flex items-center gap-3">
          <span
            className={[
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              style.iconBg,
              style.iconColor,
            ].join(' ')}
          >
            {ICON[type]}
          </span>
          <span
            className={[
              'flex-1 min-w-0 text-[13px] leading-none',
              style.titleColor,
            ].join(' ')}
          >
            {message}
          </span>
          {onApprove ? (
            <Button
              type="primary"
              size="small"
              className="shrink-0"
              disabled={false}
              loading={updating}
              onClick={() => onApprove(false)}
            >
              {t('bsc.btn.dongy', 'Đồng ý')}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <span
              className={[
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                style.iconBg,
                style.iconColor,
              ].join(' ')}
            >
              {ICON[type]}
            </span>
            <div className="flex-1 min-w-0 pt-1.5">
              <span className={['block text-[13px] leading-relaxed', style.titleColor].join(' ')}>
                {message}
              </span>
              {type === 'rejected' && reasonText ? (
                <span className={['block text-[13px] leading-relaxed mt-1.5 font-medium', style.titleColor].join(' ')}>
                  {t('bsc.label.lydo', 'Lý do')}: {reasonText}
                </span>
              ) : null}
            </div>
          </div>
          {canApproveReject && onApprove && onReject ? (
            <div className="flex flex-wrap items-center justify-end gap-2 mt-3 pt-3 border-t border-black/[0.06]">
              <Button
                type="primary"
                size="small"
                disabled={false}
                loading={updating}
                onClick={() => onApprove(isHoTro)}
              >
                {t('bsc.btn.dongy', 'Đồng ý')}
              </Button>
              <Button
                type="default"
                size="small"
                danger
                disabled={false}
                onClick={onReject}
                loading={updating}
              >
                {t('bsc.label.tuchoi', 'Từ chối')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
